import { MonitorSeriesNames } from '../../../location-card.constants';
import { alarmToggleActions, disableField, enableField } from '../../actions';
import { Section } from '../../models';
import { FlexSectionIds } from './base-config-models';
import { ConfigureAlarmsFieldId } from '../common.models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { field } from '../../core/field-schema';
import { required, maxLength, pattern, min, max } from '../../core/validator-builders';
import { AlarmDevice, FlexLocationCardState } from './models';
import { NonSensorDeviceNames } from 'app/shared/enums/device-type-code';
import { INSTALLATION_TYPE, InstallationSeries } from 'app/shared/models/location-details';
import { InstallationTypes } from 'app/shared/models/locations';

export const getValidators = function (isMetric: boolean) {
    const validators = [];

    if (isMetric) {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
            }),
        ];
    } else {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR',
            }),
        ];
    }
};

/**
 * FLEX Alarms Configuration Section
 */
export const configureAlarms = (state: FlexLocationCardState): Section => {
    const settings = state.settings;
    const config = state.monitorConfiguration;

    const { installationType } = state?.locationDetails[0];

    const { height } = state?.locationDetails[0];

    const alarmData: AlarmDevice = config?.devices.find((v) => v.name === NonSensorDeviceNames.Alarm1) as AlarmDevice;

    const flexSeries = state.allMonitorSeries.find(
        (v) => v.value.toLowerCase() === MonitorSeriesNames.flex.toLowerCase(),
    );
    const batteryLowEnabled = alarmData?.batteryLowEnable ?? true;

    const depthAlarmValidators = [required(), ...getValidators(settings.isMetric)];
    const highFlowValidators = [
        required(),
        min({ value: 0 }),
        max({ value: 100000 }),
        pattern(
            settings.isMetric
                ? {
                      value: REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern,
                      message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
                  }
                : {
                      value: REGEX_CONFIG.numeric0_100000With3DecimalPlacesPattern,
                      message: 'COMMON.NUMERIC_PRECISION_ERROR_3_DECIMALS',
                  },
        ),
    ];

    return {
        info: {
            id: FlexSectionIds.ConfigureAlarms,
            title: 'ADD_EDIT.ADD_EDIT_CONFIGURE',
            displayTitle: true,
            expandable: true,
            expanded: false,
            preserveOldValues: false,
        },
        rows: [
            // Low Level Alarm Row
            {
                info: {
                    id: 'low-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.LowLevelEnable, 'lowLevelEnable', {
                        placeholder: 'COMMON.LOW_LEVEL_ALARM',
                        value: alarmData?.lowLevelEnable ?? false,
                        panelClass: 'alarm-slide-toggle',
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.LowLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.LowLevelThreshold, 'lowLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_LOW_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData?.lowLevelThreshold,
                        disabled: alarmData && alarmData.lowLevelEnable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // Full Pipe Alarm Row
            {
                info: {
                    id: 'full-pipe-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.FullPipeEnable, 'fullPipeEnable', {
                        placeholder: 'COMMON.FULL_PIPE_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.fullPipeEnable ?? false,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold }),
                            ],
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.FullPipeThreshold, 'fullPipethreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_FULL_PIPE',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        readonly: true,
                        value: installationType === INSTALLATION_TYPE.PIPE ? height : alarmData?.fullPipeThreshold,
                        disabled: alarmData && alarmData.fullPipeEnable ? false : true,
                        validators: getValidators(settings.isMetric),
                    }),
                ],
            },

            // High Level Alarm Row
            {
                info: {
                    id: 'high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighLevelEnable, 'highLevelEnable', {
                        placeholder: 'COMMON.HIGH_LEVEL_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.highLevelEnable ?? false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighLevelThreshold, 'highLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData?.highLevelThreshold,
                        disabled: alarmData && alarmData.highLevelEnable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High-High Level Alarm Row
            {
                info: {
                    id: 'high-high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighHighEnable, 'highHighEnable', {
                        placeholder: 'COMMON.HIGH_HIGH_ALARAM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.highHighEnable ?? false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighHighThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighHighThreshold, 'highHighThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_HIGH',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData?.highHighThreshold,
                        disabled: alarmData && alarmData.highHighEnable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High Flow Alarm Row
            {
                info: {
                    id: 'high-flow-alarm-row',
                    panelClass: 'each-configure-alarm',
                    tooltip: 'COMMON.HIGH_FLOW_ALARM_TOOLTIP',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighFlowEnable, 'highFlowEnable', {
                        placeholder: 'COMMON.HIGH_FLOW_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.highFlowEnable ?? false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighFlowThreshold, highFlowValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighFlowThreshold, 'highFlowThreshold', {
                        placeholder: 'COMMON.QUANTITY_THRESHOLD_OF',
                        placeholderSuffix: settings.flowUnit,
                        panelClass: 'alarm-input',
                        value: alarmData?.highFlowThreshold,
                        disabled: alarmData && alarmData.highFlowEnable ? false : true,
                        validators: highFlowValidators,
                    }),
                ],
            },

            // Battery Low Alarm Row
            {
                info: {
                    id: 'battery-alarm-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.BatteryLowEnable, 'batteryLowEnable', {
                        placeholder: 'COMMON.BATTERY_LOW_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: batteryLowEnabled,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.BatteryLowThreshold })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.BatteryLowThreshold }),
                            ],
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.BatteryLowThreshold, 'batteryLowThreshold', {
                        placeholder: 'COMMON.BATTERY_THRESHOLD_INPUT_PLACEHOLDER',
                        placeholderSuffix: settings.voltUnit,
                        panelClass: 'alarm-input',
                        value: alarmData?.batteryLowThreshold ?? flexSeries?.batteryLowInt,
                        disabled: !batteryLowEnabled,
                        validators: [
                            required(),
                            maxLength({ value: 100 }),
                            pattern({
                                value: REGEX_CONFIG.installationValuePattern,
                                message: 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                        ],
                    }),
                ],
            },
        ],
    };
};
